import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useQuasar } from 'quasar'
import { useI18n } from 'vue-i18n'

/** Six hours - background checks for desktop updates */
const DESKTOP_UPDATE_CHECK_INTERVAL_MS = 6 * 60 * 60 * 1000

/** Debounce window focus before triggering a silent update check */
const DESKTOP_UPDATE_FOCUS_DEBOUNCE_MS = 3000

/** Max wait for a terminal update-status event after IPC succeeds */
const UPDATE_CHECK_TIMEOUT_MS = 30000

const STICKY_UPDATE_PHASES = new Set(['available', 'downloading', 'downloaded'])

export const electronAutoUpdateKey = Symbol('electronAutoUpdate')

function isDesktopUpdateDebug () {
  return !!import.meta.env.QUASAR_DEBUG
}

export function useAutoUpdate () {
  const $q = useQuasar()
  const { t } = useI18n()

  /** Toolbar phase: idle | available | downloading | downloaded */
  const updatePhase = ref('idle')
  const isChecking = ref(false)
  const isManualCheckInProgress = ref(false)
  const updateProgress = ref(0)
  const updateError = ref(null)
  const updateInfo = ref(null)

  /** @deprecated use updatePhase — kept for any legacy consumers */
  const updateStatus = computed(() => {
    if (isChecking.value && updatePhase.value === 'idle') {
      return 'checking'
    }
    return updatePhase.value
  })

  let lastCheckWasManual = false
  let intervalId = null
  let focusDebounceId = null
  let checkTimeoutId = null
  let unsubs = []

  /** Single-flight: one check at a time; concurrent callers share the same promise */
  let inFlightCheckPromise = null
  let resolveInFlightCheck = null

  const logDev = (message, detail) => {
    if (!isDesktopUpdateDebug()) return
    if (detail !== undefined) {
      console.log('[desktopUpdate]', message, detail)
    } else {
      console.log('[desktopUpdate]', message)
    }
  }

  const mapUpdaterError = (errorMsg) => {
    if (errorMsg === 'not_packaged') {
      return t('layout.desktopUpdate.notPackaged')
    }
    return errorMsg
  }

  const clearCheckTimeout = () => {
    if (checkTimeoutId != null) {
      clearTimeout(checkTimeoutId)
      checkTimeoutId = null
    }
  }

  const completeInFlightCheck = () => {
    clearCheckTimeout()
    isChecking.value = false
    if (resolveInFlightCheck) {
      resolveInFlightCheck()
      resolveInFlightCheck = null
      inFlightCheckPromise = null
    }
  }

  const notifyManualCheckFailed = (msg) => {
    $q.notify({
      type: 'negative',
      message: t('layout.desktopUpdate.checkFailed', { msg }),
      position: 'top'
    })
  }

  const armCheckTimeout = () => {
    clearCheckTimeout()
    checkTimeoutId = setTimeout(() => {
      logDev('check timed out waiting for updater status event')
      const manual = lastCheckWasManual
      completeInFlightCheck()
      isManualCheckInProgress.value = false
      if (manual) {
        notifyManualCheckFailed(t('layout.desktopUpdate.checkTimedOut'))
      }
      lastCheckWasManual = false
    }, UPDATE_CHECK_TIMEOUT_MS)
  }

  const checkForUpdates = async (options = {}) => {
    if (!window.electronAPI) return

    const manual = !!options.manual
    if (manual) {
      lastCheckWasManual = true
      isManualCheckInProgress.value = true
    }

    if (inFlightCheckPromise) {
      logDev('check skipped — already in flight', { manual })
      return inFlightCheckPromise
    }

    logDev('check started', { manual })

    inFlightCheckPromise = new Promise((resolve) => {
      resolveInFlightCheck = resolve
    })

    isChecking.value = true
    updateError.value = null
    armCheckTimeout()

    try {
      const result = await window.electronAPI.checkForUpdates()
      if (!result.success) {
        throw new Error(mapUpdaterError(result.error))
      }
      await inFlightCheckPromise
    } catch (error) {
      const msg = error.message || String(error)
      updateError.value = msg
      completeInFlightCheck()
      isManualCheckInProgress.value = false
      if (manual || lastCheckWasManual) {
        notifyManualCheckFailed(msg)
      }
      lastCheckWasManual = false
    }

    return inFlightCheckPromise
  }

  const downloadUpdate = async () => {
    if (!window.electronAPI) return

    try {
      updateError.value = null
      updatePhase.value = 'downloading'
      updateProgress.value = 0
      const result = await window.electronAPI.downloadUpdate()
      if (!result.success) {
        throw new Error(mapUpdaterError(result.error))
      }
    } catch (error) {
      const msg = error.message || String(error)
      updateError.value = msg
      updatePhase.value = 'available'
      $q.notify({
        type: 'negative',
        message: t('layout.desktopUpdate.downloadFailed', { msg }),
        position: 'top'
      })
    }
  }

  const installUpdate = async () => {
    if (!window.electronAPI) return

    try {
      const result = await window.electronAPI.installUpdate()
      if (!result.success) {
        throw new Error(mapUpdaterError(result.error))
      }
    } catch (error) {
      const msg = error.message || String(error)
      updateError.value = msg
      $q.notify({
        type: 'negative',
        message: t('layout.desktopUpdate.installFailed', { msg }),
        position: 'top'
      })
    }
  }

  const handleUpdateStatus = (status, info) => {
    logDev('status event', { status, version: info?.version })

    if (status === 'checking') {
      isChecking.value = true
      return
    }

    if (status === 'available') {
      updatePhase.value = 'available'
      if (info) {
        updateInfo.value = info
      }
      completeInFlightCheck()
      isManualCheckInProgress.value = false
      if (lastCheckWasManual) {
        const version = info?.version || ''
        $q.notify({
          type: 'info',
          message: version
            ? t('layout.desktopUpdate.versionAvailable', { version })
            : t('layout.desktopUpdate.updateNow'),
          position: 'top'
        })
        lastCheckWasManual = false
      }
      return
    }

    if (status === 'not-available') {
      if (!STICKY_UPDATE_PHASES.has(updatePhase.value)) {
        updatePhase.value = 'idle'
      }
      completeInFlightCheck()
      isManualCheckInProgress.value = false
      if (lastCheckWasManual) {
        $q.notify({
          type: 'info',
          message: t('layout.desktopUpdate.latest'),
          position: 'top'
        })
        lastCheckWasManual = false
      }
      return
    }

    if (status === 'downloaded') {
      updatePhase.value = 'downloaded'
      completeInFlightCheck()
      isManualCheckInProgress.value = false
      lastCheckWasManual = false
    }
  }

  const handleUpdateProgress = (progress) => {
    const pct = typeof progress?.percent === 'number' ? progress.percent : 0
    updateProgress.value = pct
    if (updatePhase.value !== 'downloaded') {
      updatePhase.value = 'downloading'
    }
  }

  const handleUpdateError = (error) => {
    const msg = typeof error === 'string' ? error : String(error)
    logDev('error event', msg)
    updateError.value = msg
    completeInFlightCheck()
    isManualCheckInProgress.value = false
    if (!STICKY_UPDATE_PHASES.has(updatePhase.value)) {
      updatePhase.value = 'idle'
    }
    if (lastCheckWasManual) {
      $q.notify({
        type: 'negative',
        message: t('layout.desktopUpdate.error', { msg }),
        position: 'top'
      })
      lastCheckWasManual = false
    }
  }

  const handleUpdateLog = (payload) => {
    if (!isDesktopUpdateDebug() || !payload) return
    const level = payload.level || 'info'
    const message = payload.message || ''
    const fn = level === 'error'
      ? console.error
      : level === 'warn'
        ? console.warn
        : console.log
    fn('[autoUpdater]', message)
  }

  const scheduleFocusCheck = () => {
    if (focusDebounceId != null) {
      clearTimeout(focusDebounceId)
    }
    focusDebounceId = setTimeout(() => {
      focusDebounceId = null
      logDev('focus debounce — silent check')
      void checkForUpdates({ manual: false })
    }, DESKTOP_UPDATE_FOCUS_DEBOUNCE_MS)
  }

  onMounted(() => {
    if (!window.electronAPI) return

    unsubs = [
      window.electronAPI.onUpdateStatus(handleUpdateStatus),
      window.electronAPI.onUpdateProgress(handleUpdateProgress),
      window.electronAPI.onUpdateError(handleUpdateError)
    ]

    if (typeof window.electronAPI.onUpdateLog === 'function') {
      unsubs.push(window.electronAPI.onUpdateLog(handleUpdateLog))
    }

    window.addEventListener('focus', scheduleFocusCheck)

    void checkForUpdates({ manual: false })

    intervalId = setInterval(() => {
      void checkForUpdates({ manual: false })
    }, DESKTOP_UPDATE_CHECK_INTERVAL_MS)
  })

  onUnmounted(() => {
    if (intervalId != null) {
      clearInterval(intervalId)
      intervalId = null
    }
    if (focusDebounceId != null) {
      clearTimeout(focusDebounceId)
      focusDebounceId = null
    }
    clearCheckTimeout()
    window.removeEventListener('focus', scheduleFocusCheck)
    for (const u of unsubs) {
      try {
        u?.()
      } catch {
        // ignore
      }
    }
    unsubs = []
  })

  return {
    updatePhase,
    updateStatus,
    isChecking,
    isManualCheckInProgress,
    updateProgress,
    updateError,
    updateInfo,
    checkForUpdates,
    downloadUpdate,
    installUpdate
  }
}
